import { Box, Group, Stack, Text } from '@mantine/core';

/**
 * MessageBubble — a single user message in an Exchange thread.
 *
 * Per design pack §5.5: own messages right-aligned, counterparty left-aligned.
 * Bubbles are kept low-chrome — no avatars in v1; the alignment + label do
 * the speaker disambiguation.
 *
 * The body is rendered as plain text (whitespace preserved). No markdown
 * v1; richer rendering is a polish-pass concern.
 */
export type MessageBubbleProps = {
  body: string;
  fromSelf: boolean;
  /** Display name of the author (counterparty for left-aligned bubbles). */
  authorLabel: string;
  /** Unix ms timestamp of message creation. */
  createdAt: number;
  /** Unix ms timestamp of last edit, if any. */
  editedAt?: number | null;
  /** True when the OTHER party has marked this read. Only meaningful on own bubbles. */
  readByCounterparty?: boolean;
};

export function MessageBubble({
  body,
  fromSelf,
  authorLabel,
  createdAt,
  editedAt,
  readByCounterparty,
}: MessageBubbleProps) {
  const align = fromSelf ? 'flex-end' : 'flex-start';
  const bg = fromSelf
    ? 'light-dark(var(--mantine-color-brand-2), var(--mantine-color-dark-3))'
    : 'light-dark(var(--mantine-color-gray-1), var(--mantine-color-dark-5))';
  const time = formatTime(createdAt);

  return (
    <Stack gap={2} align={align} my="xs" data-testid="message-bubble">
      {!fromSelf && (
        <Text size="xs" c="dimmed" px="xs">
          {authorLabel}
        </Text>
      )}
      <Box
        style={{
          maxWidth: '80%',
          padding: '8px 12px',
          background: bg,
          borderRadius: 12,
          // Tail-ish corner on the speaker side.
          borderTopRightRadius: fromSelf ? 4 : 12,
          borderTopLeftRadius: fromSelf ? 12 : 4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          // BUBBLE elevation — subtle lift off the message stream, both schemes.
          boxShadow:
            'light-dark(0 1px 2px rgba(33,28,22,0.06), 0 1px 2px rgba(0,0,0,0.30))',
        }}
      >
        <Text size="sm" component="div">
          {body}
        </Text>
      </Box>
      <Group gap={6} px="xs">
        <Text size="xs" c="dimmed">
          {time}
        </Text>
        {editedAt !== null && editedAt !== undefined && (
          <Text size="sm" c="gray.5" fs="italic">
            edited
          </Text>
        )}
        {fromSelf && readByCounterparty === true && (
          <Text size="xs" c="dimmed">
            · read
          </Text>
        )}
        {fromSelf && readByCounterparty === false && (
          <Text size="xs" c="dimmed">
            · sent
          </Text>
        )}
      </Group>
    </Stack>
  );
}

function formatTime(ms: number): string {
  // Short, locale-aware. The thread title carries the date context so we only
  // need wall-clock time on individual bubbles.
  try {
    return new Date(ms).toLocaleTimeString(undefined, {
      hour: 'numeric',
      minute: '2-digit',
    });
  } catch {
    return '';
  }
}
